"use client";

import {
  type HTMLAttributeReferrerPolicy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Loader from "../../atoms/Loader";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";
import { IframeProps } from "components/Iframe/iframe.props";

const IframeSkeleton = () => (
  <div
    className="w-full h-[500px] bg-gray-100 rounded-lg flex items-center justify-center"
    data-testid="iframe-skeleton"
  >
    <Loader />
  </div>
);

const IframeError = ({ onRetry }: { onRetry: () => void }) => (
  <div
    className="w-full h-[500px] bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center"
    data-testid="iframe-error"
  >
    <div className="text-gray-500 mb-4">Failed to load content</div>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
      data-testid="iframe-retry"
    >
      Retry
    </button>
  </div>
);

export const Default = (props: IframeProps) => {
  console.log("---->[Iframe] Component", props.fields);

  const { fields, params, rendering } = props;

  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Lazy load the iframe when it scrolls into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Defer load slightly after intersection so critical content paints first
  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setShouldLoad(true), 256);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isInView]);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleRetry = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    setShouldLoad(false);
    setTimeout(() => setShouldLoad(true), 100);
  }, []);

  if (!fields) {
    return <NoDataFallback componentName="Iframe" />;
  }

  const additionalParams = new URLSearchParams(
    fields.AdditionalIframeAttributes?.value || "",
  );
  const iframeAttributes: Record<string, string> = Object.fromEntries(
    additionalParams.entries(),
  );

  const iframeUrl = fields.IframeUrl?.value?.href;
  const iframeDomain = iframeUrl ? new URL(iframeUrl).origin : null;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const enhancedAttributes = {
    ...iframeAttributes,
    loading: "lazy" as const,
    title: iframeAttributes.title || "Embedded content",
    allow: iframeAttributes.allow || "encrypted-media",
    referrerPolicy: (iframeAttributes.referrerPolicy ||
      "strict-origin-when-cross-origin") as HTMLAttributeReferrerPolicy,
    sandbox:
      iframeAttributes.sandbox || "allow-scripts allow-same-origin allow-forms",
  };

  const widthAttr = rendering?.params?.Width ?? params?.Width;
  const heightAttr = rendering?.params?.Height ?? params?.Height;
  const { title: iframeTitle, ...attrsWithoutTitle } = enhancedAttributes;

  const renderContent = () => {
    if (!shouldLoad) {
      return <IframeSkeleton />;
    }
    if (hasError) {
      return <IframeError onRetry={handleRetry} />;
    }
    return (
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 z-10">
            <IframeSkeleton />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title={iframeTitle}
          width={widthAttr}
          height={heightAttr}
          className={`${widthAttr ? "" : "w-full"} ${
            heightAttr ? "" : "h-[500px]"
          } transition-opacity duration-300 max-w-full ${
            isLoading ? "opacity-0" : "opacity-100"
          }`}
          data-testid="iframe-element"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          {...attrsWithoutTitle}
        />
      </div>
    );
  };

  return (
    <>
      {iframeDomain && (
        <>
          <link rel="preconnect" href={iframeDomain} crossOrigin="" />
          <link rel="dns-prefetch" href={iframeDomain} />
        </>
      )}
      <div
        ref={containerRef}
        className={`iframe-component mx-auto px-4 ${
          params?.Styles?.includes("pt-0") ? "pt-0" : "pt-[64px]"
        } pb-[60px] w-full ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
        data-testid="iframe-container"
      >
        {renderContent()}
      </div>
    </>
  );
};
